import { mapWithConcurrency } from "@/utils/concurrency";

export type PageQuery = {
  page?: number;
  size?: number;
};

export type PageResp<Item> = {
  items: Item[];
  total: number;
  page: number;
  size: number;
};

// 카탈로그 전량 적재의 페이지 크기. BE PAGE_SIZE_MAX 와 같은 상한이라 더 늘릴 수 없다.
const CATALOG_PAGE_SIZE = 100;

/**
 * 기본 동시 페이지 요청 수. 브라우저의 HTTP/1.1 origin 연결 한도와 같다.
 *
 * HTTP/1.1 에서는 이보다 많이 보내도 브라우저 큐에서 기다릴 뿐이다. HTTP/2 에서는 모든 요청이
 * 한꺼번에 서버로 가서, 같은 순간 보낸 다른 화면 요청을 늦춘다. 카탈로그 네 개(labels·
 * embodiments·tips·camera-channels)를 `Promise.all` 로 함께 부르는 곳이 있다. 상한이 없으면
 * 네 카탈로그의 요청 합계가 그대로 나간다.
 */
const CATALOG_PAGE_CONCURRENCY = 6;

export type FetchAllPagesOptions = {
  /**
   * 동시에 보낼 페이지 요청 수(기본 {@link CATALOG_PAGE_CONCURRENCY}).
   *
   * 다음 조건을 모두 만족하는 경로만 이 값을 늘린다:
   * - 사용자가 시작한 bulk 조작처럼 그 순간 화면에 다른 요청이 없다.
   * - 페이지 수가 수백이다.
   * 작은 기본값을 쓰면 라운드 수만큼 RTT 가 쌓인다.
   */
  concurrency?: number;
};

/**
 * 페이지네이션 컬렉션을 전량 적재한다(picker·facet·자동완성처럼 전 목록이 필요한 곳).
 *
 * 첫 페이지로 `total` 을 확인한다. 나머지 페이지는 서로 독립이라 동시에 보낸다.
 * 순차로 보내면 페이지 수만큼 RTT 가 쌓인다(`docs/rules.md` §7). 그러나 모든 요청을 한꺼번에
 * 보내지는 않는다. 수십 건짜리 카탈로그와 다운로드의 파일 목록(meta·extra·Annotation Set)이
 * 이 헬퍼를 함께 쓴다. 그래서 페이지 수와 관계없이 동시 요청 수를 `concurrency` 로 제한한다.
 *
 * **건수·페이지 수에는 상한을 두지 않는다.** 상한을 넘을 때 할 수 있는 동작은 모두 현재보다
 * 나쁘다:
 * - throw 하면 카탈로그 하나 때문에 필터 바 전체가 표시되지 않는다.
 * - 잘라내면 호출부가 `total` 로 계산한 페이지네이션이 경고 없이 틀린다.
 * 규모가 커졌다는 신호는 도메인을 아는 호출부가 낸다(`api/label.ts` 의 클라 적재 한계 경고).
 */
export async function fetchAllPages<Item>(
  fetchPage: (query: PageQuery) => Promise<PageResp<Item>>,
  { concurrency = CATALOG_PAGE_CONCURRENCY }: FetchAllPagesOptions = {},
): Promise<Item[]> {
  const firstPage = await fetchPage({ page: 1, size: CATALOG_PAGE_SIZE });
  if (firstPage.items.length >= firstPage.total) {
    return [...firstPage.items];
  }

  // total 이 남았는데 첫 페이지가 비었으면 뒤 페이지도 받을 것이 없다. 무한 요청 대신 멈춘다.
  if (firstPage.items.length === 0) {
    throw new Error("Catalog pagination did not advance.");
  }

  const restCount = Math.ceil(firstPage.total / CATALOG_PAGE_SIZE) - 1;
  // 페이지 순서는 응답 도착 순서와 관계없어야 한다. mapWithConcurrency 가 인덱스 순서를 보장한다.
  const restPages = await mapWithConcurrency(
    Array.from({ length: restCount }, (_, index) => index + 2),
    (page) => fetchPage({ page, size: CATALOG_PAGE_SIZE }),
    concurrency,
  );

  return [firstPage, ...restPages].flatMap((response) => response.items);
}
